export type Billing = 'monthly' | 'yearly'

export interface Plan {
    id: 'Free' | 'Plus' | 'Private'
    name: string
    tagline: string
    monthly: number
    yearly: number
    cta: string
    href: string
    highlighted?: boolean
    features: Array<string>
}

export const PLANS: Array<Plan> = [
    {
        id: 'Free',
        name: 'Free',
        tagline: 'Bring every account into one clear view.',
        monthly: 0,
        yearly: 0,
        cta: 'Begin for free',
        href: '/login?mode=signup&plan=Free',
        features: [
            'Link up to 3 accounts',
            'Budgets with automatic categories',
            '20 questions to the AI each month',
            'A 30-day view of your cash flow',
            'First advisor call on us'
        ]
    },
    {
        id: 'Plus',
        name: 'Plus',
        tagline: 'The complete AI advisor, day and night.',
        monthly: 14,
        yearly: 11,
        cta: 'Try Plus free for 14 days',
        href: '/login?mode=signup&plan=Plus',
        highlighted: true,
        features: [
            'No limit on accounts or questions',
            '90-day forecasts with smart alerts',
            'Investing that rebalances itself',
            'Goal tracking and what-if scenarios',
            'Support at the front of the queue'
        ]
    },
    {
        id: 'Private',
        name: 'Private',
        tagline: 'Plus a certified advisor of your own.',
        monthly: 49,
        yearly: 39,
        cta: 'Start with Private',
        href: '/login?mode=signup&plan=Private',
        features: [
            'All of Plus, included',
            'Your own certified advisor',
            'A planning session every quarter',
            'Help with tax and estate plans',
            'Share with up to 4 family members'
        ]
    }
]

export type CellValue = boolean | string

export const COMPARISON: Array<{ group: string, rows: Array<{ label: string, values: [CellValue, CellValue, CellValue] }> }> = [
    {
        group: 'Everyday money',
        rows: [
            { label: 'Connected accounts', values: ['Up to 3', 'Unlimited', 'Unlimited'] },
            { label: 'Budgets with auto categories', values: [true, true, true] },
            { label: 'Net worth over time', values: [true, true, true] },
            { label: 'Reminders before bills are due', values: [true, true, true] }
        ]
    },
    {
        group: 'The AI advisor',
        rows: [
            { label: 'Questions to the AI each month', values: ['20', 'Unlimited', 'Unlimited'] },
            { label: 'How far forecasts look ahead', values: ['30 days', '90 days', '12 months'] },
            { label: 'Proactive smart alerts', values: [false, true, true] },
            { label: 'What-if scenarios', values: [false, true, true] }
        ]
    },
    {
        group: 'Investing',
        rows: [
            { label: 'Portfolios managed for you', values: [false, true, true] },
            { label: 'Hands-off rebalancing', values: [false, true, true] },
            { label: 'Tax-loss harvesting', values: [false, true, true] },
            { label: 'Portfolios that match your values', values: [false, true, true] }
        ]
    },
    {
        group: 'Human advice',
        rows: [
            { label: 'Calls with an advisor', values: ['First call on us', 'First call on us', 'Unlimited'] },
            { label: 'An advisor of your own', values: [false, false, true] },
            { label: 'Tax and estate guidance', values: [false, false, true] },
            { label: 'How we support you', values: ['Email', 'Priority', 'Direct line'] }
        ]
    }
]

export const FAQS = [
    {
        question: 'Does the Free plan ever start costing money?',
        answer: 'Never. Free has no end date and never asks for a card. Move up only when you want unlimited questions, longer forecasts or investing that runs itself.'
    },
    {
        question: 'What happens during the Plus trial?',
        answer: 'You get 14 days of Plus at no cost, with no card needed to begin. If the trial ends and you have not chosen a plan, you simply settle back onto Free.'
    },
    {
        question: 'Can I change plans down the road?',
        answer: 'Whenever you like, up or down. Moving up applies the moment you choose it, and moving down applies once your current billing period ends.'
    },
    {
        question: 'How do Verdane advisors earn their living?',
        answer: 'Every Verdane advisor is on salary. No one earns a commission on any product, so their only reason to suggest something is that it moves you forward.'
    },
    {
        question: 'How is my information protected?',
        answer: 'Everything is encrypted in storage and in transit. Account connections are read-only, money moves only when you approve it, and nothing is ever sold. Export or delete it at any time.'
    },
    {
        question: 'And if I decide to leave?',
        answer: 'Download a full copy of your data whenever you want. After you close your account, we permanently erase everything within 30 days.'
    }
]
